import { ArrowRight } from "lucide-react";
import { memo } from "react";
import {
	Box,
	Button,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from "@mui/material";

import { HeadCell, headCells, Row, TableHeadersProps } from "./DashboardUtils";
import DropdownIcon from "../../assets/icons/filter_dropdown_icon.svg";
import MedicalBed from "../../assets/icons/medical-bed.svg";
import capitalize from "../../utils/capitalize";
import { useDashboard } from "./useDashboard";
import "./Dashboard.scss";

const customIcon = () => {
	return (
		<Box className="sort-icon">
			<img
				width={10}
				src={DropdownIcon}
			/>
		</Box>
	);
};

function TableHeaders(props: TableHeadersProps) {
	const { onRequestSort, order, orderBy } = props;

	const createSortHandler = (property: keyof Row) => (event: React.MouseEvent<unknown>) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead className="table__header">
			<TableRow>
				{headCells.map((headCell: HeadCell) => {
					return (
						<TableCell
							key={headCell.id}
							sx={{ width: `${headCell.width}rem !important` }}
							className="table__header__option"
							sortDirection={orderBy === headCell.id ? order : false}
						>
							<TableSortLabel
								active={orderBy === headCell.id}
								direction={orderBy === headCell.id ? order : "asc"}
								onClick={createSortHandler(headCell.id)}
								IconComponent={customIcon}
							>
								{headCell.label}
							</TableSortLabel>
						</TableCell>
					);
				})}
			</TableRow>
		</TableHead>
	);
}

const _getPillNurse = (classX: string) => {
	const pills = {
		"ENF A": "pill-nurse--purple",
		"ENF B": "pill-nurse--blue",
		"ENF C": "pill-nurse--green",
		"ENF D": "pill-nurse--yellow--variant",
		COORD: "pill-nurse--yellow",
		"ENF ESP": "pill-nurse--rose",
		"AUX A": "pill-nurse--purple--variant",
		"AUX B": "pill-nurse--blue--variant",
		"AUX C": "pill-nurse--green--variant",
		AUX: "pill-nurse--rose--variant",
		"ENF AUX": "pill-nurse--gray",
		"ENF SUB": "pill-nurse--gray--variant",
		"": "pill-nurse--empty",
	};

	return <Box className={`pill-nurse ${pills[classX as keyof typeof pills]}`}>{classX}</Box>;
};

function Dashboard() {
	const { order, orderBy, rows, isLoading, onChangeSort, navigateToPatients } = useDashboard();

	return (
		<Box className="dashboard">
			<Box className="table-container">
				{rows.length > 0 ? (
					<TableContainer
						component={Paper}
						className="table"
						id="onboarding-13"
					>
						<Table
							size="small"
							stickyHeader
							sx={{ tableLayout: "auto" }}
						>
							<TableHeaders
								order={order}
								orderBy={orderBy}
								onRequestSort={onChangeSort}
							/>
							<TableBody>
								{rows.map((row: Row, index: number) => {
									return (
										<TableRow
											hover
											key={`row-${index}`}
											className="table__row"
										>
											<TableCell className="table__cell">
												{row.room}
											</TableCell>
											<TableCell className="table__cell">
												{row.category}
											</TableCell>
											<TableCell className="table__cell">
												<Box display={"flex"}>
													{_getPillNurse(row.nurseClassx)}
													{capitalize(row.nurseName)}
												</Box>
											</TableCell>
											<TableCell
												className={`table__cell ${
													row.secondaryNurseName === "" &&
													"table__cell--empty"
												}`}
											>
												<Box display={"flex"}>
													{row.secondaryNurseName !== "" &&
														_getPillNurse(row.secondaryNurseClassx)}
													{capitalize(row.secondaryNurseName)}

													{row.secondaryNurseName === "" &&
														"Sin profesional asigando"}
												</Box>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					<Box className="table table--empty">
						{isLoading ? (
							<Box>
								<Box className="custom-loader" />
							</Box>
						) : (
							<>
								<img src={MedicalBed} />
								<Typography className="table--empty__title">
									Aún no hay profesionales asignados.
								</Typography>
								<Typography className="table--empty__subtitle">
									Asegúrate de hacer la asignación de pacientes del turno.
								</Typography>

								<Button
									className="table--empty__button"
									onClick={navigateToPatients}
								>
									<ArrowRight />
									Ir a Pacientes
								</Button>
							</>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
}

export default memo(Dashboard);
